import {Component, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import { SearchService } from '../search.service';
import { AuthService } from '../../auth/auth.service';
import { ActivatedRoute } from '@angular/router';
import { SwirlService } from '../swirl.service';


@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
  standalone: false,
  encapsulation: ViewEncapsulation.None
})
export class SummaryComponent implements OnInit, OnDestroy {
  @Input() ragRatingEnabled: boolean = false;

  text: string = '';
  provider: string = '';
  nameMappings: { [key: string]: any } = {
    'CHATGPT': 'ChatGPT'
  };
  isLoading: boolean = false;
  isShowing: boolean = false;
  lastSearchId: any;
  originalSearchId: any;
  originalSearchQuery: string = '';
  originalSearchProviders: string = '';
  ragItems: any;
  isRagParamInUrl: any;
  qSummaryTime: number;
  qSummaryTimeSeconds: number;
  aiModel: string | boolean;
  resultId: number | undefined;
  rated = false;
  showFeedbackBox = false;
  showOtherModal = false;
  showThanksMessage = false;
  feedbackText = '';
  suggestions = [
    'I don\'t like the style',
    'It\'s incorrect',
    'It didn\'t answer my question',
    'It\'s too long',
    'It\'s too biased',
    'Contains unsafe information',
    'Took too long to answer'
  ];
  thumbsUpIcon          = 'assets/images/icon_thumbs_up.png';
  thumbsUpActiveIcon    = 'assets/images/icon_thumbs_up_active.png';
  thumbsDownIcon        = 'assets/images/icon_thumbs_down.png';
  thumbsDownActiveIcon  = 'assets/images/icon_thumbs_down_active.png';
  lastRating: 0 | 1 | null = null;

  constructor(
    private authService: AuthService,
    private searchService: SearchService,
    private route: ActivatedRoute,
    private swirl: SwirlService,

  ) {
    this.text = '';
    this.provider = '';
    this.lastSearchId = false;
    this.originalSearchId = false;
    this.originalSearchQuery = '';
    this.originalSearchProviders = '';
    this.ragItems = [];
    this.isRagParamInUrl = false;
    this.qSummaryTime = 0;
    this.qSummaryTimeSeconds = 0;
    this.aiModel = false;
    this.resultId = 0;
  }

  private handleRagResponse(response: any) {
    let messageContent = '';
    let additionalContent: any = {};

    if (response.ai_summary && Array.isArray(response.ai_summary) && response.ai_summary.length > 0) {
      messageContent = response.ai_summary[0].body[0];
      additionalContent = response.ai_summary[0].additional_content || {};
    } else {
      messageContent = response.message;
      additionalContent = response.additional_content || {};
    }

    let sanitizedMessage = messageContent;

    // AI model
    if (response.ai_model) {
      this.aiModel = response.ai_model;
    } else if (additionalContent.ai_model) {
      this.aiModel = additionalContent.ai_model;
    }

    // Follow-up questions
    if (
      additionalContent.followUps &&
      Array.isArray(additionalContent.followUps) &&
      additionalContent.followUps.length > 0
    ) {
      let followUpsHtml = `<div class="followups-list"><p class="followups-header">Follow-up Questions:</p><ul class="followups-list-ul">`;
      additionalContent.followUps.forEach((item: any) => {
        const rawQuestion = item.question || '';
        const cleanQuestion = rawQuestion.replace(/<[^>]+>/g, '').replace('?', '').trim();
        const encodedQuestion = encodeURIComponent(cleanQuestion);
        const currentQuery = this.route.snapshot.queryParamMap.get('q');

        let link = `/galaxy/search?q=${currentQuery}`;

        const searchInfo = this.searchService.searchInfo.value?.search;
        if (searchInfo?.searchprovider_list) {
          const providers = searchInfo.searchprovider_list.join(',');
          link += `&providers=${encodeURIComponent(providers)}`;
        }

        link += '&rag=true';

        const prompt = this.route.snapshot.queryParamMap.get('prompt') || '';
        if (prompt) {
          link += `&prompt=${encodeURIComponent(prompt)}`;
        }

        link += `&ai_instructions=${encodedQuestion}`;

        followUpsHtml += `<li class="followups-list-li">
                            <a class="question-link" href="${link}">${rawQuestion}</a>
                          </li>`;
      });
      followUpsHtml += `</ul></div>`;
      sanitizedMessage += followUpsHtml;
    }

    // Sources
    if (
      additionalContent.sources &&
      Array.isArray(additionalContent.sources) &&
      additionalContent.sources.length > 0
    ) {
      let sourcesHtml = `<div class="sources-list"><p class="sources-header">Sources:</p><ul class="sources-list-ul">`;
      additionalContent.sources.forEach((source: any) => {
        const cleanUrl = source.url.replace(/<em>/gi, '').replace(/<\/em>/gi, '');
        const cleanTitle = source.title.replace(/<em>/gi, '').replace(/<\/em>/gi, '');
        sourcesHtml += `<li class="sources-list-li">
                          <a class="sources-list-link" target="_blank" href="${cleanUrl}">
                            <span class="me-2">${cleanTitle}</span>
                          </a>
                        </li>`;
      });
      sourcesHtml += `</ul></div>`;
      sanitizedMessage += sourcesHtml;
    }
    this.text = sanitizedMessage;
    setTimeout(() => {
      this.addFavicons();
    }, 0);
  } 

  initiateRestDataFlow(searchId: any) {
    // Clear previous AI metadata before starting new search
    this.searchService.clearRequestSpecs();
    
    const query = this.route.snapshot.queryParamMap.get('q') || '';
    const providers = this.route.snapshot.queryParamMap.get('providers')?.split(',') || [];
    const prompt = this.route.snapshot.queryParamMap.get('prompt') || '';
    const aiInstructions = this.route.snapshot.queryParamMap.get('ai_instructions') || '';
    const restRequestStart = Date.now();
    
    // Get timeout from URL or use default from swirl service
    const urlTimeout = this.route.snapshot.queryParamMap.get('rag_timeout');
    const timeoutValue = urlTimeout ? parseInt(urlTimeout, 10) : this.swirl.timeout;

    this.swirl.getDetailSearchRag(
      searchId,
      query,
      providers,
      prompt,
      aiInstructions,
      this.ragItems,
      timeoutValue
    ).subscribe({
      next: (response) => {
        const elapsed = Date.now() - restRequestStart;
        this.searchService.restResponseTime.next(elapsed);
        this.handleRagResponse(response);
        if (this.authService.getRagRatingStatus()) {
          this.resultId = response.result_id;
          this.rated = response.rated;
          this.lastRating = null;
          this.ragRatingEnabled = true;
        } else {
          this.ragRatingEnabled = false;
        }
        this.searchService.setRagLoading(false);
      },
      error: (err) => {
        // Handle timeout errors specifically
        let errorMessage = "An error occurred while fetching RAG data.";
        if (err.status === 504) {
          errorMessage = this.swirl.timeoutText;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        this.handleRagResponse({"message": errorMessage});
        this.isLoading = false;
        this.isShowing = true;
        this.resultId = 0;
        this.rated = true;
      }
    });

  }

  getFaviconUrl(url: string): string {
    const cleanUrl = url.replace(/<em>/gi, '').replace(/<\/em>/gi, '');
    try {
      const domain = new URL(cleanUrl).origin;
      return `${domain}/favicon.ico`;
    } catch (e) {
      return '';
    }
  }

  private styleSources(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const sourcesHeader = Array.from(doc.querySelectorAll('b')).find(
      (b) => b.innerText.trim() === 'Sources:'
    );
    if (sourcesHeader) {
      if (sourcesHeader.previousSibling?.nodeName === 'BR') {
        sourcesHeader.previousSibling.remove();
      }
      const headerWrapper = document.createElement('p');
      headerWrapper.className = 'sources-header';
      headerWrapper.innerHTML = sourcesHeader.outerHTML;
      sourcesHeader.replaceWith(headerWrapper);
    }
    return doc.body.innerHTML;
  }

  private addFavicons(): void {
    const sourceItems = document.querySelectorAll('.sources-list-li');
    sourceItems.forEach((item) => {
      const link = item.querySelector('a');
      if (link) {
        const href = link.getAttribute('href');
        if (href) {
          try {
            const domain = new URL(href).origin;
            const faviconUrl = `${domain}/favicon.ico`;
  
            const img = document.createElement('img');
            img.src = faviconUrl;
            img.alt = `${domain} favicon`;
            img.className = 'source-favicon';
            img.onerror = () => {
              img.style.display = 'none';
            };
  
            // Prepend the favicon image to the <li> if not already present.
            if (!item.querySelector('img.source-favicon')) {
              item.prepend(img);
            }
          } catch (e) {
            // Do nothing if URL construction fails
          }
        }
      }
    });
  }

  ngOnInit() {
    // Subscribe to REST RAG results
    this.searchService.isRagLoading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.searchService.isRagShowing.subscribe(isShowing => {
      this.isShowing = isShowing;
    });
    this.searchService.ragResults?.subscribe((response: any) => {
      if (response) {
        this.handleRagResponse(response);
        if (this.authService.getRagRatingStatus()) {
          this.resultId = response.result_id;
          this.rated = response.rated;
          this.lastRating = null;
          this.ragRatingEnabled = true;
        } else {
          this.ragRatingEnabled = false;
        }
        this.isLoading = false;
        this.isShowing = true;
        this.aiModel = response.ai_model || false;
      }
    });
    this.searchService.restResponseTime.subscribe((time) => {
      this.qSummaryTime = time;
      this.qSummaryTimeSeconds = Math.round(time / 100) / 10;
    });

    this.route.queryParamMap.subscribe(params => {
      this.isRagParamInUrl = params.get('rag');
    });
    this.searchService.selectedRagItems.subscribe((ragItems: any) => this.ragItems = ragItems);
    this.searchService.currentSearchInfo.subscribe(async (info) => {
      if (info?.search?.id && this.isRagParamInUrl) {
        const currentSearchId = info.search.id;
        // Get query from search info or fallback to route parameters
        const currentQuery = info.search.query || this.route.snapshot.queryParamMap.get('q') || '';

        // Check if this is a new search by comparing only the query
        // Don't consider provider changes as a new search for RAG purposes
        const isNewSearch = (!this.originalSearchId && !this.originalSearchQuery) || 
                          (currentQuery && this.originalSearchQuery && currentQuery !== this.originalSearchQuery);

        if (isNewSearch) {
          // New search - update tracking info and trigger RAG
          this.originalSearchId = currentSearchId;
          this.originalSearchQuery = currentQuery;
          this.originalSearchProviders = info.search.providers ? info.search.providers.join(',') : '';
          this.lastSearchId = currentSearchId;
          this.searchService.setRagLoading(true);
          this.searchService.setRagShowing(true);
          this.initiateRestDataFlow(currentSearchId);
        }
      }
    });

    // Also subscribe to search context for more reliable query tracking
    this.searchService.currentSearchContext.subscribe(searchContext => {
      if (searchContext.query && this.isRagParamInUrl && !this.originalSearchQuery) {
        // If we don't have an original query yet, set it from the search context
        this.originalSearchQuery = searchContext.query;
      }
    });

    this.searchService.eraseEvent.subscribe((isErased) => {
      if (isErased) {
        this.isLoading = true;
        this.lastSearchId = false;
        this.originalSearchId = false; // Reset original search ID on erase
        this.originalSearchQuery = '';
        this.originalSearchProviders = '';
        this.ragItems = [];
        this.searchService.clearRequestSpecs();
      }
    });

    this.searchService.isLoading.subscribe(isLoading => {
      this.lastSearchId = !isLoading ? this.lastSearchId : false;
    });
  }

  ngOnDestroy() {}

  onThumbsUp() {
    if (this.rated) { return; }
    this.swirl.rateResult(this.resultId, 1)
        .subscribe({
          next: () => {
            this.rated = true;
            this.lastRating = 1;
            this.showThanksMessage = true;
            setTimeout(() => this.showThanksMessage = false, 3000);
          },
          error: err => console.error('API Validation Errors:', err.error)
        });
  }

  onThumbsDown() {
    if (this.rated) { return; }
    this.lastRating = 0;
    this.showFeedbackBox = true;
  }

  openOtherModal(): void {
    if (this.rated) return;
    this.showOtherModal = true;
  }

  closeOtherModal(): void {
    this.showOtherModal = false;
    this.feedbackText = '';
  }

  submitFeedback(feedback: string) {
    if (this.rated || !feedback.trim()) { return; }
    this.swirl.rateResult(this.resultId, 0, feedback.trim())
        .subscribe({
          next: () => {
            this.rated = true;
            this.showFeedbackBox = false;
            this.lastRating = 0;
            this.showThanksMessage = true;
            setTimeout(() => this.showThanksMessage = false, 3000);
          },
          error: err => console.error('API Validation Errors:', err.error)
        });
  }
}
